import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { LoginPage } from './Login/LoginPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { RegisterPage } from './Register/RegisterPage';
import { MainPage } from './Main/MainPage';
import { NotFoundPage } from './ErrorPages/NotFoundPage';
import { UserPage } from './User/UserPage';
import { SettingsPage } from './Settings/SettingsPage';
import { ForgotPasswordPage } from './ForgotPassword/ForgotPasswordPage';
import { ResetPasswordPage } from './ForgotPassword/ResetPasswordPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { EncryptionProvider } from './context/EncryptionContext';

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: ${(props) => props.theme.colors.background};
`;

const StyledSpinner = styled(Spinner)`
  color: ${(props) => props.theme.colors.primary};
`;

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <LoadingWrapper>
        <StyledSpinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </StyledSpinner>
      </LoadingWrapper>
    );
  }

  // Register/forgot-password/reset-password must stay reachable regardless
  // of auth state — e.g. a stale/lingering session in this browser
  // shouldn't 404 someone who followed a "Register here" link from the
  // native app expecting to create a new account.
  const publicAuthRoutes = [
    {
      path: "/register",
      element: <RegisterPage/>,
    },
    {
      path: "/forgot-password",
      element: <ForgotPasswordPage/>,
    },
    {
      path: "/reset-password",
      element: <ResetPasswordPage/>,
    },
  ];

  let router;
  if (user) {
    router = createBrowserRouter([
      ...publicAuthRoutes,
      {
        path: "/",
        element: <MainPage/>,
      },
      {
        path: "/user",
        element: <UserPage/>,
        handle: { breadcrumb: "topNav.user" },
      },
      {
        path: "/settings",
        element: <SettingsPage/>,
        handle: { breadcrumb: "topNav.settings" },
      },
      {
        path: "*",
        element: <NotFoundPage/>,
      },
    ]);

    return (
      <EncryptionProvider>
        <RouterProvider router={router}/>
      </EncryptionProvider>
    );
  } else {
    router = createBrowserRouter([
      ...publicAuthRoutes,
      {
        path: "*",
        element: <LoginPage/>,
      },
    ]);
  }

  return <RouterProvider router={router} />;
};

function App() {
  return (
    <AuthProvider>
      <AppRoutes/>
    </AuthProvider>
  );
}

export default App;
